import type { AccommodationSubTypeId } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/accommodation-sub-type-id";
import {
    accommodationSubTypeIdPrefix,
    accommodationSubTypeIdZodSchema,
} from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/accommodation-sub-type-id";
import {
    accommodationTypeIdZodSchema,
    type AccommodationTypeId,
} from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/accommodation-type-id";
import { translatableStringFactory } from "@@/shared/translations/translation";
import { z } from "zod";

type AccommodationTypeAndSubTypeId =
    `${AccommodationTypeId}__${AccommodationSubTypeId}`;

const parseAccommodationTypeAndSubTypeIds = (
    id: string | AccommodationTypeAndSubTypeId,
): readonly [AccommodationTypeId, AccommodationSubTypeId] => {
    const subTypeIdIndex = id.indexOf(accommodationSubTypeIdPrefix);
    const result = [
        id.slice(0, subTypeIdIndex - 2) as AccommodationTypeId, // remove the "__" as well;
        id.slice(subTypeIdIndex) as AccommodationSubTypeId,
    ] as const;
    if (!accommodationTypeIdZodSchema.safeParse(result[0]).success) {
        throw new Error(`Invalid AccommodationTypeId in ${id}`);
    }
    if (!accommodationSubTypeIdZodSchema.safeParse(result[1]).success) {
        throw new Error(`Invalid AccommodationSubTypeId in ${id}`);
    }
    return result;
};

const accommodationTypeAndSubTypeIdZodSchema = z.string().refine(
    value => {
        try {
            parseAccommodationTypeAndSubTypeIds(value);
            return true;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_error) {
            return false;
        }
    },
    translatableStringFactory({
        sv: "Ogiltigt id för en boendetyp",
        en: "Invalid accommodation type id",
    }),
); // improve this maybe?
const accommodationTypeAndSubTypeIdFactory = (
    typeId: AccommodationTypeId,
    subTypeId: AccommodationSubTypeId,
): AccommodationTypeAndSubTypeId => `${typeId}__${subTypeId}`;

export {
    accommodationTypeAndSubTypeIdFactory,
    accommodationTypeAndSubTypeIdZodSchema,
};
export type { AccommodationTypeAndSubTypeId };
